import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import { useUpdateProfile } from '../../../../hooks/profile/useUpdateProfile'

import { Button } from '../../../buttons/Button'
import FormSection from '../../../formSection/FormSection'
import { CheckboxInput } from '../../../input/checkboxInput/CheckboxInput'

import { useApp } from '../../../../contexts/AppContext'
import { useProfileContext } from '../../../../contexts/ProfileContext'
import { BannerKind } from '../../../../types/contexts/AppContext'

type TermsConsentProps = {
  onAccepted?: () => void
  saveLabel?: string
}

const TermsConsent = ({ onAccepted, saveLabel }: TermsConsentProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('account')
  const { showBanner } = useApp()
  const { profile, updateLocalProfile } = useProfileContext()
  const { updateProfile, loading, error } = useUpdateProfile()
  const [accepted, setAccepted] = useState(false)
  const termsAlreadyAccepted = profile?.termsConsent === true

  useEffect(() => {
    if (error) {
      showBanner({ kind: BannerKind.ERR, msg: error })
    }
  }, [error, showBanner])

  const handleAccept = async () => {
    if (!accepted) {
      return
    }

    const updatedProfile = await updateProfile([
      { op: 'add', path: '/termsConsent', value: true },
    ])

    if (!updatedProfile) {
      return
    }

    updateLocalProfile({ termsConsent: updatedProfile.termsConsent })
    showBanner({ kind: BannerKind.OK, msg: t('account.terms.saved') })
    if (onAccepted) {
      onAccepted()
    } else {
      navigate('/account/profile', { replace: true })
    }
  }

  return (
    <FormSection
      title={t('account.terms.section-title')}
      subtitle={t(
        termsAlreadyAccepted
          ? 'account.terms.accepted-subtitle'
          : 'account.terms.section-subtitle'
      )}
      actions={
        termsAlreadyAccepted ? undefined : (
          <Button
            label={saveLabel ?? t('account.terms.accept')}
            variant="primary"
            size="sm"
            block
            loading={loading}
            disabled={!accepted || loading}
            onClick={handleAccept}
          />
        )
      }
    >
      {termsAlreadyAccepted ? (
        <>
          <div className="account-terms-status">
            <div className="account-terms-document">
              <span>{t('account.terms.document-label')}</span>
              <Link
                to="/account/terms-and-conditions"
                className="account-terms-title-link"
              >
                {t('account.terms.section-title')}
              </Link>
            </div>
            <span className="badge badge-success">
              <span className="dot-status account-terms-status-dot" />
              {t('account.terms.accepted-label')}
            </span>
          </div>
        </>
      ) : (
        <div className="account-terms-form">
          <CheckboxInput
            name="termsConsent"
            className="account-terms-checkbox"
            required
            checked={accepted}
            disabled={loading}
            onChange={(_, value) => setAccepted(value)}
            label={
              <Trans
                i18nKey="account:account.terms.checkbox"
                components={{
                  terms: <Link to="/account/terms-and-conditions" />,
                }}
              />
            }
          />
        </div>
      )}
    </FormSection>
  )
}

export default TermsConsent
